/**
 * CapabilityState — Platform-wide truthful capability state component
 *
 * UX1A Platform Primitive. Replaces FinanceCapabilityState as the canonical
 * truth-surface for all modules (Finance, HCM, Learn, etc.).
 *
 * States:
 *   COMING_SOON         — capability planned, not yet in this wave
 *   NOT_CONFIGURED      — requires admin configuration
 *   NOT_CONNECTED       — no canonical data connection exists for this capability
 *   NO_DATA             — connection exists but no records yet (not an error)
 *   UNMAPPED            — data exists but cannot be canonically mapped
 *   NOT_AVAILABLE       — architecturally absent for this institution
 *   BLOCKED             — upstream authority gate prevents this capability
 *   LEGACY              — module uses a non-canonical data source
 *   PERMISSION_DENIED   — user role does not have access to this capability
 *
 * Usage:
 *   <CapabilityState state="COMING_SOON" title="Fee Setup" />
 *   <CapabilityState state="BLOCKED" title="Per-Program KPI"
 *     reason="Requires enrollment authority (ENR1)." />
 *   <CapabilityState state="LEGACY" compact />
 */
import React from "react";
import {
  Clock,
  Settings,
  Unlink,
  XCircle,
  Lock,
  Database,
  ShieldOff,
  InboxIcon,
} from "lucide-react";

const CONFIGS = {
  COMING_SOON: {
    icon: Clock,
    iconClass: "text-blue-500",
    bgClass: "bg-blue-50 border border-blue-200",
    badge: "COMING SOON",
    badgeClass: "bg-blue-100 text-blue-700",
    defaultTitle: "Not Yet Available",
    defaultReason: "This capability is planned for a future wave.",
  },
  NOT_CONFIGURED: {
    icon: Settings,
    iconClass: "text-amber-500",
    bgClass: "bg-amber-50 border border-amber-200",
    badge: "NOT CONFIGURED",
    badgeClass: "bg-amber-100 text-amber-700",
    defaultTitle: "Configuration Required",
    defaultReason:
      "An administrator must complete configuration before this capability is available.",
  },
  NOT_CONNECTED: {
    icon: Unlink,
    iconClass: "text-slate-500",
    bgClass: "bg-slate-50 border border-slate-200",
    badge: "NOT CONNECTED",
    badgeClass: "bg-slate-100 text-slate-600",
    defaultTitle: "Not Connected",
    defaultReason:
      "No canonical data connection exists for this capability. Connect a data source to enable this view.",
  },
  NO_DATA: {
    icon: InboxIcon,
    iconClass: "text-slate-400",
    bgClass: "bg-slate-50 border border-slate-100",
    badge: "NO DATA",
    badgeClass: "bg-slate-100 text-slate-500",
    defaultTitle: "No Data Available",
    defaultReason:
      "The data connection is live but there are no records yet for this institution.",
  },
  UNMAPPED: {
    icon: Unlink,
    iconClass: "text-slate-500",
    bgClass: "bg-slate-50 border border-slate-200",
    badge: "UNMAPPED",
    badgeClass: "bg-slate-100 text-slate-600",
    defaultTitle: "Data Not Mapped",
    defaultReason:
      "This data exists but cannot be mapped to a canonical source without additional authority.",
  },
  NOT_AVAILABLE: {
    icon: XCircle,
    iconClass: "text-red-500",
    bgClass: "bg-red-50 border border-red-200",
    badge: "NOT AVAILABLE",
    badgeClass: "bg-red-100 text-red-700",
    defaultTitle: "Not Available",
    defaultReason: "This capability is not available for this institution.",
  },
  BLOCKED: {
    icon: Lock,
    iconClass: "text-purple-500",
    bgClass: "bg-purple-50 border border-purple-200",
    badge: "BLOCKED",
    badgeClass: "bg-purple-100 text-purple-700",
    defaultTitle: "Blocked",
    defaultReason:
      "A required upstream authority is missing. Review the blocking dependency.",
  },
  LEGACY: {
    icon: Database,
    iconClass: "text-amber-600",
    bgClass: "bg-amber-50 border border-amber-200",
    badge: "LEGACY SOURCE",
    badgeClass: "bg-amber-100 text-amber-700",
    defaultTitle: "Legacy Data Source",
    defaultReason:
      "This capability currently uses a legacy data source and is not yet connected to the canonical AcademiaOS record.",
  },
  PERMISSION_DENIED: {
    icon: ShieldOff,
    iconClass: "text-slate-500",
    bgClass: "bg-slate-50 border border-slate-200",
    badge: "ACCESS RESTRICTED",
    badgeClass: "bg-slate-100 text-slate-600",
    defaultTitle: "Access Restricted",
    defaultReason:
      "Your current role does not have permission to view this capability.",
  },
};

export function CapabilityState({
  state = "COMING_SOON",
  title,
  reason,
  compact = false,
  "data-testid": testId,
}) {
  const cfg = CONFIGS[state] || CONFIGS.COMING_SOON;
  const Icon = cfg.icon;

  if (compact) {
    return (
      <div
        data-testid={testId || `capability-state-${state.toLowerCase()}`}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium ${cfg.bgClass}`}
      >
        <Icon className={`h-3 w-3 ${cfg.iconClass}`} />
        <span className={cfg.iconClass}>{title || cfg.defaultTitle}</span>
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide ${cfg.badgeClass}`}
        >
          {cfg.badge}
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid={testId || `capability-state-${state.toLowerCase()}`}
      className={`flex flex-col items-center justify-center gap-3 p-8 rounded-lg text-center ${cfg.bgClass}`}
    >
      <div
        className={`p-3 rounded-full bg-white/70 shadow-sm`}
      >
        <Icon className={`h-8 w-8 ${cfg.iconClass}`} />
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-foreground">
            {title || cfg.defaultTitle}
          </p>
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wider ${cfg.badgeClass}`}
          >
            {cfg.badge}
          </span>
        </div>
        <p className="text-xs text-muted-foreground max-w-xs">
          {reason || cfg.defaultReason}
        </p>
      </div>
    </div>
  );
}
